/**
 * The delivery-date cell content that opens the future-dates modal for a line with several ship
 * dates or a customer-part TBD date. Shows the line's requested date with a calendar icon when it
 * has one; otherwise "TBD" and a calendar (customer-part TBD line) or a warning icon and
 * "Multiple Dates". Also shows the "Values have been updated" popper when the line's requested
 * date was just corrected (`FutureDatesHelpers.tsx` → `updateRequestedDate`).
 *
 * Rendered by the delivery-date cells of the cart
 * (`ShoppingCart/ShoppingCartTable/ShoppingCartTableDeliveryDateCell.tsx`), checkout
 * (`Shared/CheckoutOrderDetails/CheckoutOrderDetailsTable/Helper/CheckoutOrderDetailsTableDeliveryDateCell.tsx`),
 * quote edit (`QuoteDetails/Helpers/EditProductsTableComponents/TableRequestedDeliveryDate.tsx`)
 * and Easy Quote (`EasyQuote/Helper/TableComponents/EasyQuoteTableDeliveryDateCell.tsx`). The
 * modal itself is `FutureDatesModal.tsx`, rendered once per page by the caller.
 */
import React, { Dispatch, SetStateAction, useEffect } from "react";
import { Flex } from "@ryerson/frontend.layout";
import { Icon } from "@ryerson/frontend.assets";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { useApplication } from "@ryerson/frontend.application";
import { FutureDatesContent } from "./FutureDatesContent";
import { DraftQuoteLineItem, Popper, ShoppingCartDataType } from "@ryerson/frontend.common";
import { parseDateSafely } from "@components/Helper/common";
import { DateFormat, formatDateToString } from "@components/Helper/utilities";
import { getPopperColor, popperContent } from "../DatePickerPopper/DatePickerPopper";
import { Alert } from "@ryerson/frontend.notification";

type FutureDatesTriggerProps = {
	isExpanded: boolean;
	modalOpen: boolean;
	setModalOpen: Dispatch<SetStateAction<boolean>>;
	rowData: ShoppingCartDataType | DraftQuoteLineItem;
	isMobile?: boolean;
	/** True after the line's requested date was corrected; shows the popper, reset after 4 s. */
	displayDatesUpdated?: boolean;
	setDisplayDatesUpdated?: React.Dispatch<React.SetStateAction<boolean>>;
	/**
	 * Row identifier (the cart passes the row index); builds the popper id `notifier-popper-<id>`.
	 */
	id: number;
	/** Show "TBD" and a calendar instead of "Multiple Dates" when there is no requested date. */
	shouldShowTBDIcon: boolean;
};

/** Delivery-date trigger for one line; see the file header. Clicking toggles `modalOpen`. */
export const FutureDatesTrigger: React.FC<FutureDatesTriggerProps> = ({
	isExpanded,
	modalOpen,
	setModalOpen,
	rowData,
	isMobile = false,
	displayDatesUpdated,
	setDisplayDatesUpdated,
	id,
	shouldShowTBDIcon,
}) => {
	const { theme } = useTheme();
	const { white, gray, lightGray, link, primaryBrand } = theme.colors.primary;
	const { lightBlue } = theme.colors.secondary;
	const {
		localization: { language },
	} = useApplication();
	const content = FutureDatesContent[language || "en"];

	const triggerRef = React.useRef<HTMLDivElement>();
	const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
	const [popperReady, setPopperReady] = React.useState(false);

	const setTriggerRef = React.useCallback((node: HTMLDivElement) => {
		triggerRef.current = node;
		if (node) setAnchorEl(node);
	}, []);

	React.useEffect(() => {
		if (anchorEl) setPopperReady(true);
	}, [anchorEl]);

	const poppercontent = popperContent[language || "en"];

	useEffect(() => {
		if (displayDatesUpdated) {
			if (setDisplayDatesUpdated) {
				setTimeout(() => {
					setDisplayDatesUpdated(false);
				}, 4000);
			}
		}
	}, [displayDatesUpdated]);

	const requestedDeliveryDate = rowData.requestedDeliveryDate;
	/**
	 * Using timezone-safe parsing eliminates the need for +1 day compensation
	 * Original +1 day was needed because new Date(string) caused timezone shifts
	 * using Luxon which returned UTC , which we are trying to remove for local date handling
	 */
	const requestedDate = parseDateSafely(requestedDeliveryDate) || new Date();
	// Fix: Use correct formatDateToString syntax for MM/DD/YYYY format
	const displayDate = formatDateToString(requestedDate, DateFormat["MM/DD/YYYY"]);

	const showDisplayText = () =>
		shouldShowTBDIcon ? (
			<Flex alignItems="center" css={{ paddingRight: "10px" }}>
				<Typography color={isExpanded ? white : primaryBrand} size={isMobile ? "md" : "sm"}>
					{content.tbd}
				</Typography>
				&nbsp;
				<Icon icon={"calendar"} size={isMobile ? "sm" : "xs"} color={primaryBrand} />
			</Flex>
		) : (
			<>
				<Icon icon={"warning"} size="sm" />
				&nbsp;
				<Typography color={isExpanded ? white : lightBlue}>
					{content.multipleDates}
				</Typography>
			</>
		);

	return (
		<div ref={setTriggerRef}>
			<Flex
				onClick={() => {
					setModalOpen(!modalOpen);
				}}
			>
				{!requestedDeliveryDate ? (
					showDisplayText()
				) : (
					<>
						<Flex
							css={{
								background: white,
								color: gray,
								gap: isMobile ? 5 : 10,
								border: isMobile ? `solid ${lightGray} 1px` : "",
								borderRadius: 2,
								padding: isMobile ? 12 : 10,
								height: isMobile ? 40 : 30,
							}}
							justifyContent="center"
							alignItems="center"
							alignContent="center"
							onClick={() => {
								setModalOpen(!modalOpen);
							}}
						>
							<Typography
								size={isMobile ? "lg" : "sm"}
								color={gray}
								variant={isMobile ? "div" : "span"}
							>
								{displayDate}
							</Typography>
							<Icon icon={"calendar"} size={isMobile ? "sm" : "xs"} color={link} />
						</Flex>
					</>
				)}
			</Flex>
			{!!anchorEl && (
				<Popper
					{...{
						id: `notifier-popper-${id}`,
						targetEl: anchorEl,
						isDisplaying: !!displayDatesUpdated && popperReady,
						backColor: getPopperColor("background", theme),
						offsetY: isMobile ? -20 : 10,
						offsetX: isMobile ? -50 : 0,
						borderColor: getPopperColor("border", theme),
						placement: isMobile ? "bottom-end" : "bottom-start",
						onClick: () => {
							setDisplayDatesUpdated && setDisplayDatesUpdated(false);
						},
					}}
				>
					<>
						<div css={{ width: 240 }}>
							<Alert size="sm" severity={"info"}>
								<Typography color={getPopperColor("text", theme)}>
									{poppercontent.quantityRecalculatedNotify}
								</Typography>
							</Alert>
						</div>
					</>
				</Popper>
			)}
		</div>
	);
};
